// src/components/GestioneUtenti.js
import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

const GestioneUtenti = ({ ruoloCorrente = 'admin' }) => {
  const [utenti, setUtenti] = useState([]);
  const [form, setForm] = useState({ nome: '', email: '', password: '', ruolo: 'utente', sede_id: '', sedi_gestite: [] });
  const [editing, setEditing] = useState({});
  const [showPasswordField, setShowPasswordField] = useState({}); // per mostrare il campo
  const [sedi, setSedi] = useState([]);

  const canManageRoles = ruoloCorrente === 'admin';
  const canAssignResponsabileSedi = ruoloCorrente === 'admin';
  const canDelete = ruoloCorrente === 'admin';

  useEffect(() => {
    fetchUtenti();
    fetchSedi();
  }, [ruoloCorrente]);

  const fetchUtenti = async () => {
    try {
      const res = await api.get('/utenti');
      setUtenti(res.data || []);
    } catch (err) {
      console.error('Errore fetch utenti:', err);
    }
  };

  const fetchSedi = async () => {
    try {
      const res = await api.get('/sedi');
      setSedi(res.data || []);
    } catch (err) {
      console.error('Errore fetch sedi:', err);
    }
  };

  const roleOptions = canManageRoles ? ['utente', 'responsabile', 'admin'] : ['utente'];

  const cellControlStyle = { width: '100%', minWidth: '160px', boxSizing: 'border-box' };
  const formControlStyle = { minWidth: '180px', padding: '6px', boxSizing: 'border-box' };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'ruolo' && value !== 'responsabile') {
        next.sedi_gestite = [];
      }
      return next;
    });
  };

  const toggleFormSedeGestita = (sedeId) => {
    setForm((prev) => {
      const current = prev.sedi_gestite || [];
      const target = String(sedeId);
      const next = current.includes(target)
        ? current.filter((id) => id !== target)
        : [...current, target];
      return { ...prev, sedi_gestite: next };
    });
  };

  const creaUtente = async (e) => {
    e.preventDefault();
    const ruoloSelezionato = canManageRoles ? form.ruolo : 'utente';
    const payload = {
      nome: form.nome,
      email: form.email,
      password: form.password,
      ruolo: ruoloSelezionato,
      sede_id: form.sede_id || null,
    };
    if (canAssignResponsabileSedi && ruoloSelezionato === 'responsabile') {
      payload.sedi_ids = (form.sedi_gestite || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    }
    try {
      await api.post('/utenti', payload);
      fetchUtenti();
      setForm({ nome: '', email: '', password: '', ruolo: 'utente', sede_id: '', sedi_gestite: [] });
    } catch (err) {
      alert(err.response?.data?.message || 'Errore creazione utente');
    }
  };

  const handleChange = (id, field, value) => {
    setEditing((prev) => {
      const next = {
        ...prev[id],
        [field]: value,
      };
      if (field === 'ruolo' && value !== 'responsabile') {
        next.sedi_gestite = [];
      }
      return {
        ...prev,
        [id]: next,
      };
    });
  };

  const toggleEditingSedeGestita = (userId, sedeId, baseList = []) => {
    setEditing((prev) => {
      const current = prev[userId]?.sedi_gestite ?? baseList;
      const target = String(sedeId);
      const next = current.includes(target)
        ? current.filter((id) => id !== target)
        : [...current, target];
      return {
        ...prev,
        [userId]: { ...prev[userId], sedi_gestite: next },
      };
    });
  };

  const handleSalva = async (id) => {
    const utente = utenti.find((u) => u.user_id === id);
    const modifiche = editing[id] || {};
    const ruoloSelezionato = canManageRoles ? (modifiche.ruolo ?? utente.ruolo) : utente.ruolo;
    const sediGestiteSelezionate =
      modifiche.sedi_gestite !== undefined
        ? modifiche.sedi_gestite
        : (utente.sedi_gestite || []).map((id) => String(id));

    const datiAggiornati = {
      nome: modifiche.nome ?? utente.nome,
      email: modifiche.email ?? utente.email,
      ruolo: ruoloSelezionato,
      password: modifiche.password || '', // solo se modificata
      sede_id:
        modifiche.sede_id !== undefined
          ? modifiche.sede_id || null
          : utente.sede_id || null,
    };
    if (canAssignResponsabileSedi && ruoloSelezionato === 'responsabile') {
      datiAggiornati.sedi_ids = (sediGestiteSelezionate || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    }

    try {
      await api.put(`/utenti/${id}`, datiAggiornati);
      fetchUtenti();
      const newEditing = { ...editing };
      delete newEditing[id];
      setEditing(newEditing);
      setShowPasswordField((prev) => ({ ...prev, [id]: false }));
    } catch (err) {
      alert(err.response?.data?.message || 'Errore aggiornamento utente');
    }
  };

  const eliminaUtente = async (id) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    try {
      await api.delete(`/utenti/${id}`);
      fetchUtenti();
    } catch (err) {
      alert('Errore eliminazione utente');
    }
  };

  return (
    <div>
      <h3>👤 Crea nuovo utente</h3>
      <form onSubmit={creaUtente} style={{ marginBottom: '30px' }}>
        <input name="nome" placeholder="Nome" value={form.nome} onChange={handleFormChange} required style={formControlStyle} />
        <input name="email" placeholder="Email" value={form.email} onChange={handleFormChange} required style={formControlStyle} />
        <input type="password" name="password" placeholder="Password" value={form.password} onChange={handleFormChange} required style={formControlStyle} />
        {canManageRoles && (
          <select name="ruolo" value={form.ruolo} onChange={handleFormChange} style={formControlStyle}>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role === 'utente' ? 'Utente' : role === 'responsabile' ? 'Responsabile' : 'Admin'}
              </option>
            ))}
          </select>
        )}
        <select name="sede_id" value={form.sede_id} onChange={handleFormChange} style={formControlStyle}>
          <option value="">Nessuna sede</option>
          {sedi.map((s) => (
            <option key={s.sede_id} value={s.sede_id}>
              {s.nome}
            </option>
          ))}
        </select>
        {canAssignResponsabileSedi && form.ruolo === 'responsabile' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {sedi.map((s) => (
              <label key={s.sede_id} style={{ fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={form.sedi_gestite.includes(String(s.sede_id))}
                  onChange={() => toggleFormSedeGestita(s.sede_id)}
                />{' '}
                {s.nome}
              </label>
            ))}
          </div>
        )}
        <button type="submit">➕ Aggiungi</button>
      </form>

      <h3>📋 Elenco Utenti</h3>
      <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Ruolo</th>
            <th>Sede</th>
            {canAssignResponsabileSedi && <th>Sedi gestite</th>}
            <th>Password</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {utenti.map((utente) => {
            const edit = editing[utente.user_id] || {};
            const ruoloAttuale = edit.ruolo ?? utente.ruolo;
            const sediGestiteCorrenti =
              edit.sedi_gestite !== undefined
                ? edit.sedi_gestite
                : (utente.sedi_gestite || []).map((id) => String(id));
            return (
              <tr key={utente.user_id}>
                <td>
                  <input
                    value={edit.nome ?? utente.nome}
                    onChange={(e) => handleChange(utente.user_id, 'nome', e.target.value)}
                    style={cellControlStyle}
                  />
                </td>
                <td>
                  <input
                    value={edit.email ?? utente.email}
                    onChange={(e) => handleChange(utente.user_id, 'email', e.target.value)}
                    style={cellControlStyle}
                  />
                </td>
                <td>
                  {canManageRoles ? (
                    <select
                      value={ruoloAttuale}
                      onChange={(e) => handleChange(utente.user_id, 'ruolo', e.target.value)}
                      style={cellControlStyle}
                    >
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>
                          {role === 'utente' ? 'Utente' : role === 'responsabile' ? 'Responsabile' : 'Admin'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{utente.ruolo}</span>
                  )}
                </td>
                <td>
                  <select
                    value={
                      edit.sede_id !== undefined
                        ? edit.sede_id
                        : utente.sede_id ?? ''
                    }
                    onChange={(e) => handleChange(utente.user_id, 'sede_id', e.target.value)}
                    style={cellControlStyle}
                  >
                    <option value="">Nessuna sede</option>
                    {sedi.map((sede) => (
                      <option key={sede.sede_id} value={sede.sede_id}>
                        {sede.nome}
                      </option>
                    ))}
                  </select>
                </td>
                {canAssignResponsabileSedi && (
                  <td>
                    {ruoloAttuale === 'responsabile' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {sedi.map((sede) => (
                          <label key={sede.sede_id} style={{ fontSize: '0.85rem' }}>
                            <input
                              type="checkbox"
                              checked={sediGestiteCorrenti.includes(String(sede.sede_id))}
                              onChange={() =>
                                toggleEditingSedeGestita(
                                  utente.user_id,
                                  sede.sede_id,
                                  (utente.sedi_gestite || []).map((id) => String(id))
                                )
                              }
                            />{' '}
                            {sede.nome}
                          </label>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                )}
                <td>
                  {showPasswordField[utente.user_id] ? (
                    <input
                      type="password"
                      placeholder="Nuova password"
                      value={edit.password || ''}
                      onChange={(e) => handleChange(utente.user_id, 'password', e.target.value)}
                    />
                  ) : (
                    <button onClick={() => setShowPasswordField((prev) => ({ ...prev, [utente.user_id]: true }))}>
                      🔑 Cambia pass
                    </button>
                  )}
                </td>
                <td>
                  <button onClick={() => handleSalva(utente.user_id)}>💾 Salva</button>
                  {canDelete && (
                    <button onClick={() => eliminaUtente(utente.user_id)} style={{ marginLeft: '6px' }}>
                      🗑️ Elimina
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default GestioneUtenti;
